'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './MarketingBudgetContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const steps = [
  {
    number: '1',
    title: 'Enter your monthly budget',
    description:
      'Input your total marketing spend for the month. This is the amount you want to distribute across all marketing channels. The free allocator works with any budget size.',
  },
  {
    number: '2',
    title: 'Choose your business type and goal',
    description:
      'Select the business model that best matches yours and your primary marketing objective. The allocator uses industry-specific benchmarks to tailor your breakdown.',
  },
  {
    number: '3',
    title: 'Get your channel-by-channel breakdown',
    description:
      'See exactly how much to invest in each marketing channel. The free allocator gives you dollar amounts and percentages based on what works for businesses like yours.',
  },
];

const industryBenchmarks = [
  { industry: 'SaaS', range: '8% - 12%', topPerformers: '15%+', color: '#1e40af', bg: '#dbeafe' },
  { industry: 'E-Commerce', range: '5% - 12%', topPerformers: '15%+', color: '#166534', bg: '#dcfce7' },
  { industry: 'Professional Services', range: '5% - 10%', topPerformers: '12%+', color: '#5b21b6', bg: '#ede9fe' },
  { industry: 'Healthcare', range: '3% - 8%', topPerformers: '10%+', color: '#92400e', bg: '#fef3c7' },
  { industry: 'Financial Services', range: '6% - 10%', topPerformers: '12%+', color: '#1e40af', bg: '#dbeafe' },
  { industry: 'Tech Startups', range: '15% - 25%', topPerformers: '30%+', color: '#166534', bg: '#dcfce7' },
  { industry: 'Retail', range: '3% - 8%', topPerformers: '10%+', color: '#5b21b6', bg: '#ede9fe' },
];

const companyStageData = [
  { stage: 'Startup', range: '15% - 25%', notes: 'Heavy investment needed to build awareness and acquire first customers' },
  { stage: 'Growth', range: '10% - 15%', notes: 'Scaling proven channels while testing new acquisition strategies' },
  { stage: 'Mature', range: '5% - 10%', notes: 'Maintaining market position with efficient, optimized spend' },
  { stage: 'Enterprise', range: '3% - 8%', notes: 'Brand maintenance, retention focus, and incremental growth' },
];

const budgetMistakes = [
  {
    icon: '\uD83D\uDCB8',
    title: 'Spreading budget too thin',
    description:
      'Trying to be on every channel with a limited budget means none of them get enough investment to produce meaningful results. It is better to dominate two or three channels than to barely show up on seven. Focus your free budget where your audience actually spends time.',
    stat: 'Focused budgets see 2-3x better ROI than scattered ones',
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'Ignoring attribution data',
    description:
      'Without proper attribution, you have no idea which channels are actually driving revenue. You end up over-investing in channels that look busy but do not convert, and under-investing in the ones that quietly deliver your best customers.',
    stat: '63% of marketers cannot accurately attribute revenue to channels',
  },
  {
    icon: '\uD83E\uDDEA',
    title: 'No testing budget',
    description:
      'Allocating 100% of your budget to proven channels leaves zero room for experimentation. Set aside 10-20% for testing new channels, formats, and audiences. Today\'s experiment is tomorrow\'s top-performing channel.',
    stat: 'Companies that test regularly grow 2x faster',
  },
  {
    icon: '\u2702\uFE0F',
    title: 'Cutting budget during downturns',
    description:
      'When business slows down, the instinct is to cut marketing first. But competitors who maintain or increase spend during downturns capture market share that is nearly impossible to win back later. Visibility matters most when others disappear.',
    stat: 'Brands that maintain spend during recessions recover 3x faster',
  },
  {
    icon: '\u270D\uFE0F',
    title: 'No content investment',
    description:
      'Paid ads stop working the moment you stop paying. Content marketing builds compounding assets that drive organic traffic for months or years. Businesses that skip content investment end up entirely dependent on ad spend with no safety net.',
    stat: 'Content marketing costs 62% less than paid and generates 3x more leads',
  },
  {
    icon: '\uD83D\uDD04',
    title: 'Ignoring retention channels',
    description:
      'Most budgets focus entirely on acquiring new customers while ignoring the ones already paying. Retaining an existing customer costs 5-7x less than acquiring a new one. Email marketing and loyalty programs should always have a seat at the budget table.',
    stat: 'A 5% increase in retention can boost profits by 25-95%',
  },
];

const tips = [
  {
    number: '01',
    title: 'Visualize your budget allocation with charts',
    description:
      'A clear visual breakdown of your marketing budget helps you spot imbalances instantly. Pie charts, bar charts, and budget dashboards make it easy to present your allocation to leadership and get buy-in for your strategy.',
    cta: 'Try Charts widget',
    ctaHref: '/widgets/charts',
  },
  {
    number: '02',
    title: 'Present service packages with pricing tables',
    description:
      'If you offer marketing services or manage budgets for clients, professional pricing tables help you communicate packages clearly. They reduce back-and-forth and make it easy for prospects to choose the right tier.',
    cta: 'Try Pricing Tables widget',
    ctaHref: '/widgets/pricing-tables',
  },
  {
    number: '03',
    title: 'Capture leads without increasing ad spend',
    description:
      'Before you increase your paid budget, make sure you are converting the traffic you already have. Exit-intent popups, newsletter signups, and lead magnets can dramatically increase your conversion rate at zero additional cost.',
    cta: 'Try Popup Builder widget',
    ctaHref: '/widgets/popup-builder',
  },
  {
    number: '04',
    title: 'Create urgency with countdown campaigns',
    description:
      'Limited-time offers and product launches perform significantly better with a visible countdown. Adding urgency to your campaigns can boost conversion rates by 30% or more without touching your ad budget.',
    cta: 'Try Countdown widget',
    ctaHref: '/widgets/countdown',
  },
  {
    number: '05',
    title: 'Allocate at least 10% to SEO',
    description:
      'SEO is the highest-ROI marketing channel over time. While paid ads deliver immediate results, organic search builds a compounding traffic asset. Even a modest investment in content and technical SEO pays dividends for years.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '06',
    title: 'Review and reallocate monthly',
    description:
      'Your budget allocation should not be static. Review channel performance every month and shift spend toward what is working. The best marketers treat their budget as a living document, not a set-it-and-forget-it plan.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '07',
    title: 'Track CAC by channel, not just overall',
    description:
      'Your blended customer acquisition cost hides the truth. Some channels deliver customers at $20 while others cost $200. Break down CAC by channel to see where your budget is actually working and where it is wasted.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'Build a retention budget before scaling acquisition',
    description:
      'Pouring money into acquisition while ignoring retention is like filling a leaky bucket. Email sequences, loyalty programs, and customer success initiatives cost a fraction of new customer acquisition and deliver outsized returns.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'ROAS',
    definition: 'Return on Ad Spend. The revenue generated for every dollar spent on advertising. A ROAS of 4x means you earn $4 for every $1 in ad spend.',
    formula: 'Revenue / Ad Spend',
    whenToUse: 'Evaluating paid channel profitability',
  },
  {
    term: 'CAC',
    definition: 'Customer Acquisition Cost. The total cost to acquire one new customer, including all marketing and sales expenses. Lower is better, but not at the expense of quality.',
    formula: 'Total Marketing + Sales Cost / New Customers',
    whenToUse: 'Measuring acquisition efficiency by channel',
  },
  {
    term: 'Cost Per Lead',
    definition: 'The average cost to generate one qualified lead. Varies significantly by industry and channel. Use this metric to compare lead generation channels and optimize spend.',
    formula: 'Total Spend / Number of Leads',
    whenToUse: 'Comparing lead generation channels',
  },
  {
    term: 'Marketing ROI',
    definition: 'The return on your total marketing investment. Unlike ROAS, this includes all marketing costs, not just ad spend. Accounts for content creation, tools, team salaries, and more.',
    formula: '(Revenue - Marketing Cost) / Marketing Cost x 100',
    whenToUse: 'Justifying total marketing budget to leadership',
  },
  {
    term: 'Budget Efficiency Ratio',
    definition: 'The percentage of your marketing budget that directly contributes to revenue-generating activities versus overhead and waste. High-performing teams aim for 70%+ efficiency.',
    formula: 'Revenue-Generating Spend / Total Marketing Spend x 100',
    whenToUse: 'Auditing how effectively your budget is deployed',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const MarketingBudgetContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      {/* ── How It Works ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free marketing budget allocator</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Enter your budget, select
            your business type and goal, and get an instant channel-by-channel breakdown.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── The Framework ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>The Framework</StyledSectionLabel>
          <StyledSectionHeading>
            How budget allocation is determined
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free marketing budget allocator uses a proven framework based on industry
            benchmarks, business model, and marketing objectives. Here is the core principle.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Key Principle</p>
            <p className="formula">Allocate 40-60% to proven ROI channels, 20-30% to growth experiments, 10-20% to brand building</p>
            <p className="example">
              Example: <span>$10,000 budget = $5,000 proven channels + $2,500 experiments + $2,500 brand</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              Smart budget allocation is not about dividing your money equally across channels.
              It is about putting the most money behind the channels that deliver the best
              results for your specific business type and goals. The allocations in this free
              tool are based on aggregated performance data from thousands of businesses.
            </p>
            <p>
              The majority of your budget should go to channels with a proven track record of
              delivering ROI for your business. These are the channels where you already know
              the cost per acquisition and can predict outcomes. For a B2B SaaS company, that
              might be content marketing and paid search. For a D2C brand, it could be social
              ads and influencer partnerships.
            </p>
            <p>
              Reserve 20-30% for growth experiments. These are new channels, untested audiences,
              or emerging platforms that could become your next top performer. Without this
              testing budget, you will never discover new opportunities and your growth will
              plateau.
            </p>
            <p>
              The remaining 10-20% should go to brand building. Brand awareness does not convert
              immediately, but it lowers your cost per acquisition across every other channel
              over time. Companies that skip brand investment pay more for every lead and sale.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      {/* ── Budget Benchmarks by Industry ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Industry Benchmarks</StyledSectionLabel>
          <StyledSectionHeading>
            Marketing budget benchmarks by industry in 2026
          </StyledSectionHeading>
          <StyledSectionIntro>
            How much should you spend on marketing? These benchmarks show marketing budget
            as a percentage of revenue by industry. Use them to validate whether your total
            spend is in the right ballpark.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Industry</th>
                  <th>% of Revenue</th>
                  <th>Top Performers</th>
                </tr>
              </thead>
              <tbody>
                {industryBenchmarks.map((row) => (
                  <tr key={row.industry}>
                    <td className="col-name">{row.industry}</td>
                    <td>
                      <StyledBadge color={row.color} bg={row.bg}>
                        {row.range}
                      </StyledBadge>
                    </td>
                    <td className="col-value">{row.topPerformers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Sources: Gartner CMO Spend Survey, Deloitte CMO Survey, 2025/2026 averages.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── Budget by Company Stage ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Budget by Company Stage</StyledSectionLabel>
          <StyledSectionHeading>
            How marketing budgets shift as your company grows
          </StyledSectionHeading>
          <StyledSectionIntro>
            Your company stage determines how aggressively you should invest in marketing.
            Early-stage companies need to spend more to establish market presence, while
            mature businesses can optimize for efficiency.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Company Stage</th>
                  <th>% of Revenue</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {companyStageData.map((row) => (
                  <tr key={row.stage}>
                    <td className="col-name">{row.stage}</td>
                    <td className="col-value">{row.range}</td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Sources: SaaS Capital, FirstPageSage, 2025/2026 benchmarks.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── Common Budget Mistakes ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Common Budget Mistakes</StyledSectionLabel>
          <StyledSectionHeading>
            Six mistakes that waste your marketing budget
          </StyledSectionHeading>
          <StyledSectionIntro>
            Most marketing budgets are not too small. They are just poorly allocated. These
            are the most common mistakes that silently drain your return on investment.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {budgetMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── 8 Tips for Smarter Budget Allocation ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Smarter Budget Allocation</StyledSectionLabel>
          <StyledSectionHeading>
            8 tips for smarter marketing budget allocation
          </StyledSectionHeading>
          <StyledSectionIntro>
            These strategies help you get more results from every dollar you spend. All
            CommonNinja widgets mentioned below are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} {'→'}
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── Marketing Metrics Glossary ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Marketing Metrics Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Key marketing budget metrics compared
          </StyledSectionHeading>
          <StyledSectionIntro>
            Different metrics answer different questions about your marketing spend.
            Here is how they compare and when to use each one.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Definition</th>
                  <th>Formula</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {/* ── Related Articles ── */}
      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on marketing budgets</StyledSectionHeading>
            <StyledSectionIntro>
              Dig deeper into the strategies behind allocating your marketing budget
              and maximizing your return on investment.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article {'→'}</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
